import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BarChart3, FileText, Package, Scale, Users, Fish, Calendar, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getReport } from "@/services/localDatabase";

const MONTHS_IT = [
  "Gen", "Feb", "Mar", "Apr", "Mag", "Giu",
  "Lug", "Ago", "Set", "Ott", "Nov", "Dic"
];

const Report = () => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [anno, setAnno] = useState(null);

  useEffect(() => {
    fetchReport(null);
  }, []);

  const fetchReport = async (selectedYear) => {
    try {
      setLoading(true);
      const data = await getReport(selectedYear);
      setReport(data);
      setAnno(data.anno);
    } catch (error) {
      console.error("Error loading report:", error);
      toast.error("Errore nel caricamento del report");
    } finally {
      setLoading(false);
    }
  };

  const onYearChange = (value) => {
    const y = parseInt(value, 10);
    setAnno(y);
    fetchReport(y);
  };

  const maxMonthlyPeso = useMemo(() => {
    if (!report?.per_mese) return 1;
    const m = Math.max(...report.per_mese.map((x) => x.peso_totale));
    return m > 0 ? m : 1;
  }, [report]);

  const exportCSV = () => {
    if (!report) return;
    const rows = [];
    rows.push(["Report annuale RM DDR", `Anno: ${report.anno}`]);
    rows.push([]);
    rows.push(["Totali"]);
    rows.push(["Numero DDR", "Peso totale (kg)", "Colli totali"]);
    rows.push([report.totali.num_ddr, report.totali.peso_totale, report.totali.colli_totali]);
    rows.push([]);
    
    const dumpSection = (title, items) => {
      rows.push([title]);
      rows.push(["Voce", "N° DDR", "Peso (kg)", "Colli"]);
      items.forEach((r) => rows.push([r.label, r.num_ddr, r.peso_totale, r.colli_totali]));
      rows.push([]);
    };
    
    dumpSection("Per Causale", report.per_causale);
    dumpSection("Per Specie", report.per_specie);
    dumpSection("Per Destinatario", report.per_destinatario);
    
    rows.push(["Per Mese"]);
    rows.push(["Mese", "N° DDR", "Peso (kg)", "Colli"]);
    report.per_mese.forEach((r) => rows.push([MONTHS_IT[r.mese - 1], r.num_ddr, r.peso_totale, r.colli_totali]));
    
    const csv = rows.map((r) => r.map((c) => {
      const s = String(c == null ? "" : c);
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Report_RMDDR_${report.anno}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Report CSV scaricato");
  };

  if (loading && !report) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="report-loading">
        Caricamento report...
      </div>
    );
  }

  const stats = report?.totali || { num_ddr: 0, peso_totale: 0, colli_totali: 0 };
  const anniDisponibili = report?.anni_disponibili || [];
  const noData = stats.num_ddr === 0;

  return (
    <div className="p-6 md:p-8 lg:p-12 fade-in" data-testid="report-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-barlow tracking-tight">
            Report Annuale
          </h1>
          <p className="text-slate-500 mt-1">Statistiche aggregate per causale, specie e destinatario</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-500" />
            <Select value={anno ? String(anno) : ""} onValueChange={onYearChange}>
              <SelectTrigger className="w-[160px]" data-testid="report-year-select">
                <SelectValue placeholder="Seleziona anno" />
              </SelectTrigger>
              <SelectContent>
                {anniDisponibili.length === 0 && anno && (
                  <SelectItem value={String(anno)}>{anno}</SelectItem>
                )}
                {anniDisponibili.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={exportCSV}
            disabled={noData}
            className="border-orange-300 text-orange-600 hover:bg-orange-50"
            data-testid="report-export-csv"
          >
            <Download className="w-4 h-4 mr-2" />
            Esporta CSV
          </Button>
        </div>
      </div>

      {/* Totali cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-slate-100 border-orange-200 bg-orange-50/30" data-testid="totale-ddr-card">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Totale DDR</p>
              <p className="text-3xl font-bold text-slate-900 mt-1 font-barlow">{stats.num_ddr}</p>
            </div>
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-100 border-green-200 bg-green-50/30" data-testid="totale-peso-card">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Peso Totale</p>
              <p className="text-3xl font-bold text-slate-900 mt-1 font-barlow">{stats.peso_totale} <span className="text-base text-slate-500">kg</span></p>
            </div>
            <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
              <Scale className="w-6 h-6 text-white" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-100 border-blue-200 bg-blue-50/30" data-testid="totale-colli-card">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Colli Totali</p>
              <p className="text-3xl font-bold text-slate-900 mt-1 font-barlow">{stats.colli_totali}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
          </CardContent>
        </Card>
      </div>

      {noData ? (
        <Card className="border-slate-100">
          <CardContent className="p-12 text-center">
            <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nessun DDR registrato per l'anno {anno}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Per Causale */}
          <Card className="border-slate-100" data-testid="report-per-causale">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle className="font-barlow text-lg font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-500" />
                Per Causale di Trasporto
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ReportTable rows={report.per_causale} totale={stats.peso_totale} />
            </CardContent>
          </Card>

          {/* Per Specie */}
          <Card className="border-slate-100" data-testid="report-per-specie">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle className="font-barlow text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Fish className="w-5 h-5 text-orange-500" />
                Per Specie
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ReportTable rows={report.per_specie} totale={stats.peso_totale} />
            </CardContent>
          </Card>

          {/* Per Destinatario */}
          <Card className="border-slate-100" data-testid="report-per-destinatario">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle className="font-barlow text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-500" />
                Per Destinatario
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ReportTable rows={report.per_destinatario} totale={stats.peso_totale} />
            </CardContent>
          </Card>

          {/* Per Mese - bar chart */}
          <Card className="border-slate-100" data-testid="report-per-mese">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle className="font-barlow text-lg font-semibold text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-500" />
                Andamento Mensile (peso kg)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex items-end gap-2 h-48">
                {report.per_mese.map((m) => {
                  const height = maxMonthlyPeso > 0 ? (m.peso_totale / maxMonthlyPeso) * 100 : 0;
                  return (
                    <div key={m.mese} className="flex-1 flex flex-col items-center gap-1" data-testid={`mese-${m.mese}`}>
                      <div className="text-[10px] font-medium text-slate-500 h-4">
                        {m.peso_totale > 0 ? m.peso_totale : ""}
                      </div>
                      <div className="w-full bg-slate-100 rounded-t flex items-end" style={{ height: "calc(100% - 1.25rem)" }}>
                        <div
                          className="w-full bg-orange-500 rounded-t transition-all hover:bg-orange-600"
                          style={{ height: `${height}%`, minHeight: m.peso_totale > 0 ? "2px" : "0" }}
                          title={`${MONTHS_IT[m.mese - 1]}: ${m.peso_totale} kg, ${m.num_ddr} DDR`}
                        />
                      </div>
                      <div className="text-xs font-medium text-slate-600">{MONTHS_IT[m.mese - 1]}</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const ReportTable = ({ rows, totale }) => {
  if (!rows || rows.length === 0) {
    return <div className="p-6 text-center text-slate-500">Nessun dato</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>Voce</th>
            <th className="text-right">N° DDR</th>
            <th className="text-right">Peso (kg)</th>
            <th className="text-right">Colli</th>
            <th className="text-right">% Peso</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const pct = totale > 0 ? ((r.peso_totale / totale) * 100).toFixed(1) : 0;
            return (
              <tr key={`${r.label}-${idx}`}>
                <td className="font-medium">{r.label || "—"}</td>
                <td className="text-right">{r.num_ddr}</td>
                <td className="text-right font-medium">{r.peso_totale}</td>
                <td className="text-right">{r.colli_totali}</td>
                <td className="text-right">
                  <div className="inline-flex items-center gap-2 justify-end">
                    <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-orange-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-slate-600 w-10 text-right">{pct}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Report;
